import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wind, Heart, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function BreathingTechniquesGuide() {
  const navigate = useNavigate();

  const techniques = [
    {
      name: "4-7-8 Breath (Relaxing)",
      steps: ["Inhale through nose for 4 counts", "Hold breath for 7 counts", "Exhale through mouth for 8 counts", "Repeat 4 times"],
      best: "Falling asleep, acute anxiety",
      science: "Activates parasympathetic nervous system, lowers heart rate"
    },
    {
      name: "Box Breathing (Balancing)",
      steps: ["Inhale for 4 counts", "Hold for 4 counts", "Exhale for 4 counts", "Hold for 4 counts"],
      best: "Before stressful situations, regaining focus",
      science: "Used by Navy SEALs to stay calm under pressure"
    },
    {
      name: "Belly Breathing (Grounding)",
      steps: ["Place hand on belly", "Breathe so hand rises (not chest)", "Slow exhale, belly falls", "Focus on belly movement"],
      best: "General stress relief, connecting to body",
      science: "Engages diaphragm, signals safety to brain"
    },
    {
      name: "Energizing Breath (Alertness)",
      steps: ["Inhale quickly through nose", "Exhale quickly through nose", "Keep mouth closed", "Start with 15 seconds, build up"],
      best: "Morning energy, afternoon slump",
      science: "Increases oxygen, raises alertness (don't do before bed!)"
    }
  ];

  const whenToUse = [
    { situation: "Feeling anxious", technique: "4-7-8 Breath" },
    { situation: "Before a meeting", technique: "Box Breathing" },
    { situation: "Can't fall asleep", technique: "4-7-8 Breath" },
    { situation: "Feeling scattered", technique: "Box Breathing" },
    { situation: "Physical tension", technique: "Belly Breathing" },
    { situation: "Need energy", technique: "Energizing Breath" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Breathing Technique Mastery
          </h1>
          <p className="text-xl text-secondary-600">Your Portable Stress Relief Tool</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-cyan-500 to-teal-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Wind className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-cyan-100 text-lg">
                Learn the 4-7-8 breath and practice it 3 times today: morning, midday, and evening. 
                Use it whenever you feel stress rising. Deep breathing works immediately.
              </p>
            </div>
          </div>
        </section>

        {/* Techniques */}
        <section className="space-y-6 mb-6">
          {techniques.map((tech, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <h3 className="text-xl font-bold text-cyan-700 mb-4">{tech.name}</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-secondary-500 mb-2">Steps:</p>
                  <ol className="space-y-2">
                    {tech.steps.map((step, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                        <span className="w-5 h-5 bg-cyan-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="space-y-3">
                  <div className="bg-cyan-50 rounded-lg p-3">
                    <p className="text-sm"><strong>Best for:</strong> {tech.best}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-secondary-600">{tech.science}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Quick Reference */}
        <section className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 border-2 border-cyan-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Quick Reference</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {whenToUse.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-3 flex items-center justify-between">
                <span className="text-secondary-700">{item.situation}</span>
                <span className="text-sm bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full">{item.technique}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default BreathingTechniquesGuide;
